/** One editable colour, as the Colors tab renders it. */
export interface ColorControl {
  token: string;
  label: string;
  hint?: string;
}

export interface ColorGroup {
  title: string;
  tokens: ColorControl[];
}

/**
 * Semantic colors worth exposing, grouped the way people think about them.
 *
 * A module of its own so coverage.test.ts can check it without importing the
 * page - which drags in MSAL and the whole service layer. The test matters: it
 * fails if the preview inspector can name something on screen that no control
 * here changes, which would send people to a dead end that looks like it worked.
 */
export const COLOR_GROUPS: ColorGroup[] = [
  {
    title: 'Surfaces',
    tokens: [
      { token: 'sem-surface-canvas', label: 'Page background' },
      { token: 'sem-surface-chrome', label: 'Top bar & sidebar' },
      { token: 'sem-surface-panel', label: 'Cards & panels' },
      { token: 'sem-surface-sunken', label: 'Recessed areas' },
      { token: 'sem-surface-raised', label: 'Modals & dropdowns' },
      { token: 'sem-surface-hover', label: 'Hover highlight' },
      { token: 'sem-surface-input', label: 'Field fill' },
      { token: 'sem-surface-selected', label: 'Selected row' },
      { token: 'sem-surface-overlay', label: 'Tooltip surface' },
      { token: 'sem-scrim', label: 'Modal scrim', hint: 'Dimming behind a modal' },
    ],
  },
  {
    title: 'Headers',
    tokens: [
      { token: 'sem-header-bg', label: 'Header background', hint: 'Table and drawer headers' },
      { token: 'sem-header-fg', label: 'Header text' },
      { token: 'sem-header-border', label: 'Header border' },
    ],
  },
  {
    title: 'Text',
    tokens: [
      { token: 'sem-text-heading', label: 'Headings' },
      { token: 'sem-text-body', label: 'Body text' },
      { token: 'sem-text-muted', label: 'Muted text' },
      { token: 'sem-text-label', label: 'Field labels' },
      { token: 'sem-text-disabled', label: 'Disabled text' },
      { token: 'sem-text-inverse', label: 'Text on an accent fill' },
      { token: 'sem-text-on-overlay', label: 'Text in a tooltip' },
      { token: 'sem-text-selected', label: 'Text on a selected row' },
    ],
  },
  {
    title: 'Accent & focus',
    tokens: [
      { token: 'sem-accent', label: 'Accent fill', hint: 'Primary buttons' },
      { token: 'sem-accent-hover', label: 'Accent hover' },
      { token: 'sem-accent-fg', label: 'Text on accent' },
      { token: 'sem-accent-text', label: 'Links' },
      { token: 'sem-accent-active', label: 'Accent pressed' },
      { token: 'sem-focus-ring', label: 'Focus ring' },
    ],
  },
  {
    title: 'Lines',
    tokens: [
      { token: 'sem-border', label: 'Borders' },
      { token: 'sem-border-strong', label: 'Strong borders' },
      { token: 'sem-hairline', label: 'Hairlines', hint: 'Separators inside a panel' },
    ],
  },
  {
    title: 'Status',
    tokens: [
      { token: 'sem-ok-bg', label: 'Success fill' },
      { token: 'sem-ok-fg', label: 'Success text' },
      { token: 'sem-warn-bg', label: 'Warning fill' },
      { token: 'sem-warn-fg', label: 'Warning text' },
      { token: 'sem-danger-bg', label: 'Error fill' },
      { token: 'sem-danger-fg', label: 'Error text' },
      { token: 'sem-info-bg', label: 'Info fill' },
      { token: 'sem-info-fg', label: 'Info text' },
      { token: 'sem-flag-bg', label: 'Changed-field fill' },
      { token: 'sem-flag-fg', label: 'Changed-field text' },
      { token: 'sem-flag-edge', label: 'Changed-field border' },
    ],
  },
];
